import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ReverseGeocode from './ReverseGeocode';
import { ExternalLink, MapPin, Store, Clock } from 'lucide-react';

// Fix Leaflet's default icon path issues in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createAvatarIcon = (item) => {
    const emp = item.employee || {};
    const name = emp.name || "Employee";
    const avatar = emp.avatar;
    const role = String(emp.role || "").toLowerCase();
    const designation = String(emp.designation || "").toLowerCase();
    const isLead = role === "lead" || role === "team_lead" || designation.includes("lead");
    const status = item.liveTracking?.status || "not_checked_in";

    const borderColor = status === 'tracking' ? '#10b981' : status === 'checked_in' ? '#3b82f6' : '#6b7280';
    const gradientBg = status === 'tracking' ? 'linear-gradient(135deg, #059669, #10b981)' : status === 'checked_in' ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : 'linear-gradient(135deg, #4b5563, #6b7280)';

    const standingManSvg = `<svg width="20" height="30" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="5.5" r="4.5" fill="#ffffff"/><path d="M6 13.5C6 12.12 7.12 11 8.5 11H15.5C16.88 11 18 12.12 18 13.5V20.5H15.5V33.5H13V22H11V33.5H8.5V20.5H6V13.5Z" fill="#ffffff"/></svg>`;

    const figureContent = avatar
        ? `<img src="${avatar}" style="width:100%; height:100%; object-fit:cover; border-radius:13px;" />`
        : standingManSvg;

    const html = `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer; transform: translate(-50%, -100%); font-family: system-ui, -apple-system, sans-serif;">
        <!-- Floating Name Pill -->
        <div style="display: flex; align-items: center; gap: 4px; background: rgba(15, 23, 42, 0.95); color: white; border-radius: 9999px; padding: 3px 10px; border: 2px solid ${borderColor}; box-shadow: 0 4px 14px rgba(0,0,0,0.4); white-space: nowrap; z-index: 2;">
          <span style="font-weight: 800; font-size: 11px; max-width: 120px; overflow: hidden; text-overflow: ellipsis;">${name}</span>
          ${isLead ? `<span style="background: #8b5cf6; font-size: 8px; font-weight: 900; padding: 1px 5px; border-radius: 4px; text-transform: uppercase;">Lead</span>` : ''}
        </div>

        <!-- Standing Avatar Man Character Box -->
        <div style="width: 36px; height: 44px; background: ${gradientBg}; border-radius: 16px; border: 3px solid #ffffff; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 16px rgba(0,0,0,0.35); margin-top: 3px; position: relative; z-index: 1;">
          ${figureContent}
        </div>

        <!-- Pin Pointer Arrow -->
        <div style="width: 10px; height: 10px; background: ${borderColor}; transform: rotate(45deg); margin-top: -6px; border-right: 2px solid #fff; border-bottom: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></div>
        <!-- Shadow Base -->
        <div style="width: 22px; height: 6px; background: rgba(0,0,0,0.25); border-radius: 50%; margin-top: 2px; filter: blur(1px);"></div>
      </div>
    `;

    return L.divIcon({
        html,
        className: 'custom-standing-avatar-marker',
        iconSize: [140, 80],
        iconAnchor: [70, 80],
        popupAnchor: [0, -80]
    });
};

const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom, { duration: 1.2 });
    }
  }, [center, zoom, map]);
  return null;
};

const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers && markers.length > 0) {
      const group = new L.featureGroup(markers.map(m => L.marker([m.lat, m.lng])));
      map.fitBounds(group.getBounds(), { padding: [50, 50], maxZoom: 15 });
    }
  }, [markers, map]);
  return null;
};

const LiveTrackingMap = ({ employees = [], selectedId, onSelectEmployee }) => {
  const activeEmployees = employees.filter(e => e.liveTracking?.lastLocation?.latitude && e.liveTracking?.lastLocation?.longitude);

  const selectedEmployee = activeEmployees.find(e => e.employee?._id === selectedId);
  const selectedLocation = selectedEmployee?.liveTracking?.lastLocation;

  // Default center to India
  let center = [20.5937, 78.9629];
  let zoom = 5;

  if (selectedLocation) {
      center = [selectedLocation.latitude, selectedLocation.longitude];
      zoom = 16;
  } else if (activeEmployees.length > 0) {
      center = [activeEmployees[0].liveTracking.lastLocation.latitude, activeEmployees[0].liveTracking.lastLocation.longitude];
      zoom = 12;
  }

  return (
    <div className="h-[460px] w-full rounded-2xl overflow-hidden border border-[var(--border-color)] shadow-[var(--shadow-soft)] z-0 relative bg-[var(--card-bg)]">
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {selectedLocation && <ChangeView center={center} zoom={16} />}
        {!selectedLocation && activeEmployees.length > 0 && <FitBounds markers={activeEmployees.map(e => ({ lat: e.liveTracking.lastLocation.latitude, lng: e.liveTracking.lastLocation.longitude }))} />}

        {activeEmployees.map((item) => {
          const emp = item.employee || {};
          const loc = item.liveTracking?.lastLocation || {};
          const status = item.liveTracking?.status || "not_checked_in";
          const isSelected = emp._id === selectedId;

          return (
            <Marker
              key={emp._id}
              position={[loc.latitude, loc.longitude]}
              icon={createAvatarIcon(item)}
              eventHandlers={{
                click: () => onSelectEmployee && onSelectEmployee(emp._id)
              }}
            >
              <Popup>
                <div className="font-sans p-1 min-w-[200px] max-w-[260px]">
                  <div className="flex items-center justify-between gap-2 pb-2 border-b border-gray-100">
                    <div>
                      <strong className="block text-sm font-black text-gray-900 leading-tight">{emp.name}</strong>
                      <span className="text-[10px] text-gray-500 font-semibold">{emp.employeeId || "-"}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                        status === 'tracking' ? 'bg-emerald-100 text-emerald-800' :
                        status === 'checked_in' ? 'bg-[var(--accent-soft)] text-blue-800' :
                        'bg-gray-100 text-gray-700'
                    }`}>
                      {status === 'tracking' ? 'Live' : status.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Spot Address */}
                  <div className="mt-2 text-xs font-semibold text-gray-700 leading-snug">
                    <ReverseGeocode latitude={loc.latitude} longitude={loc.longitude} />
                  </div>

                  {/* GPS Link */}
                  <div className="mt-1.5 flex items-center justify-between text-[10px] text-gray-500 font-medium">
                    <a
                      href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-600 font-mono font-bold hover:underline flex items-center gap-1"
                      title="Open in Google Maps"
                    >
                      <MapPin size={10} className="text-rose-500 shrink-0" />
                      <span>GPS: {Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}</span>
                      <ExternalLink size={9} />
                    </a>
                  </div>

                  <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500">
                    <span className="flex items-center gap-1 font-semibold">
                      <Store size={11} className="text-indigo-500" />
                      {item.fieldReport?.shopsVisited || 0} shops
                    </span>
                    {loc.capturedAt && (
                      <span className="flex items-center gap-1 font-semibold">
                        <Clock size={11} className="text-gray-400" />
                        {new Date(loc.capturedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                      </span>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default LiveTrackingMap;
